import { useState, useEffect } from 'react';
import './Admin.css';

function Admin() {
  const [activeTab, setActiveTab] = useState('overview');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    location: '',
    image: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    location: '',
    image: null,
    currentImageUrl: null
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (activeTab === 'events') {
      fetchEvents();
    }
  }, [activeTab]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/events');
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        image: files[0] || null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let imageKey = null;
      let imageUrl = null;

      // Upload image first if one was selected
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);

        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          body: imageFormData
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.error || 'Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        imageKey = uploadResult.imageKey;
        imageUrl = uploadResult.imageUrl;
      }

      // Create event with image data
      const eventData = {
        title: formData.title,
        description: formData.description,
        event_date: formData.event_date,
        location: formData.location,
        image_key: imageKey,
        image_url: imageUrl
      };

      const response = await fetch('/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create event');
      }

      // Reset form and close
      setFormData({
        title: '',
        description: '',
        event_date: '',
        location: '',
        image: null
      });
      setShowAddForm(false);

      // Refresh events list
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (event) => {
    setEditingEvent(event.id);
    // Convert datetime to format expected by datetime-local input (YYYY-MM-DDTHH:mm)
    // Treat the database date as local time, not UTC
    const eventDate = new Date(event.event_date);

    // Get local time components to avoid timezone conversion
    const year = eventDate.getFullYear();
    const month = String(eventDate.getMonth() + 1).padStart(2, '0');
    const day = String(eventDate.getDate()).padStart(2, '0');
    const hours = String(eventDate.getHours()).padStart(2, '0');
    const minutes = String(eventDate.getMinutes()).padStart(2, '0');

    const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;

    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      event_date: formattedDate,
      location: event.location || '',
      image: null,
      currentImageUrl: event.image_url || null
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setEditFormData(prev => ({
        ...prev,
        image: files[0] || null
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      let imageKey = editFormData.currentImageUrl ? undefined : null;
      let imageUrl = editFormData.currentImageUrl ? undefined : null;

      // Upload new image if one was selected
      if (editFormData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', editFormData.image);

        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          body: imageFormData
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.error || 'Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        imageKey = uploadResult.imageKey;
        imageUrl = uploadResult.imageUrl;
      }

      // Update event with image data
      const eventData = {
        title: editFormData.title,
        description: editFormData.description,
        event_date: editFormData.event_date,
        location: editFormData.location
      };

      // Only include image fields if they're being updated
      if (imageKey !== undefined) {
        eventData.image_key = imageKey;
        eventData.image_url = imageUrl;
      }

      const response = await fetch(`/api/events/${editingEvent}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update event');
      }

      // Close edit form
      setEditingEvent(null);

      // Refresh events list
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingEvent(null);
    setEditFormData({
      title: '',
      description: '',
      event_date: '',
      location: '',
      image: null,
      currentImageUrl: null
    });
  };

  const handleDeleteClick = async (event) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete event "${event.title}"?`
    );

    if (!confirmed) return;

    setError(null);
    try {
      const response = await fetch(`/api/events/${event.id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete event');
      }

      // Refresh events list
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1>EaDo Civic Admin Dashboard</h1>
        <p className="admin-subtitle">Manage site content</p>
      </header>

      <nav className="admin-nav">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'events' ? 'active' : ''}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
      </nav>

      <main className="admin-content">
        {activeTab === 'overview' && (
          <div className="admin-section">
            <h2>Dashboard Overview</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Events</h3>
                <p className="stat-number">{events.length}</p>
                <p className="stat-label">Upcoming Events</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="admin-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2>Events</h2>
                <p className="section-description">View and Manage Events</p>
              </div>
              <button
                className="admin-nav-button"
                onClick={() => setShowAddForm(!showAddForm)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  lineHeight: '1'
                }}
              >
                {showAddForm ? '×' : '+'}
              </button>
            </div>

            {showAddForm && (
              <form onSubmit={handleSubmit} style={{
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Event Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Event Image
                  </label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    Optional. Max 5MB. Accepted formats: JPEG, PNG, WebP, GIF
                  </small>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Event'}
                </button>
              </form>
            )}

            <div className="data-table-container">
              {loading && <p>Loading events...</p>}
              {error && <p style={{ color: 'red' }}>Error: {error}</p>}
              {!loading && !error && (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Location</th>
                      <th style={{ width: '50px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="no-data">No Events yet</td>
                      </tr>
                    ) : (
                      events.map((event) => (
                        editingEvent === event.id ? (
                          <tr key={event.id}>
                            <td colSpan="5" style={{ padding: '20px', backgroundColor: '#f5f5f5' }}>
                              <form onSubmit={handleEditSubmit}>
                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Title *
                                  </label>
                                  <input
                                    type="text"
                                    name="title"
                                    value={editFormData.title}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Description
                                  </label>
                                  <textarea
                                    name="description"
                                    value={editFormData.description}
                                    onChange={handleEditInputChange}
                                    rows="3"
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Event Date & Time *
                                  </label>
                                  <input
                                    type="datetime-local"
                                    name="event_date"
                                    value={editFormData.event_date}
                                    onChange={handleEditInputChange}
                                    required
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Location
                                  </label>
                                  <input
                                    type="text"
                                    name="location"
                                    value={editFormData.location}
                                    onChange={handleEditInputChange}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Event Image
                                  </label>
                                  {editFormData.currentImageUrl && (
                                    <div style={{ marginBottom: '10px' }}>
                                      <img
                                        src={editFormData.currentImageUrl}
                                        alt="Current event"
                                        style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px' }}
                                      />
                                      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>Current image</p>
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                                    onChange={handleEditInputChange}
                                    style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                                  />
                                  <small style={{ color: '#666', fontSize: '12px' }}>
                                    {editFormData.currentImageUrl ? 'Upload a new image to replace the current one' : 'Optional. Max 5MB. Accepted formats: JPEG, PNG, WebP, GIF'}
                                  </small>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <button
                                    type="submit"
                                    disabled={submitting}
                                    style={{
                                      padding: '10px 20px',
                                      backgroundColor: '#007bff',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: submitting ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    {submitting ? 'Updating...' : 'Update Event'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    style={{
                                      padding: '10px 20px',
                                      backgroundColor: '#6c757d',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </td>
                          </tr>
                        ) : (
                          <tr key={event.id}>
                            <td>{new Date(event.event_date).toLocaleDateString()}</td>
                            <td>{event.title}</td>
                            <td>{event.description}</td>
                            <td>{event.location}</td>
                            <td style={{ display: 'flex', gap: '5px' }}>
                              <button
                                className="action-icon"
                                onClick={() => handleEditClick(event)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  padding: '5px'
                                }}
                                title="Edit event"
                              >
                                ✏️
                              </button>
                              <button
                                className="action-icon"
                                onClick={() => handleDeleteClick(event)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '16px',
                                  padding: '5px'
                                }}
                                title="Delete event"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        )
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Admin;
